import { z } from 'zod';
/**
 * Kontrakt medzi MU-pluginom a backendom.
 *
 * MU-plugin nemá vlastný testovací šev — overuje sa PRÁVE touto schémou.
 * Keď sa tu niečo zmení, musí sa zmeniť aj `wordpress/ametica-watcher.php`
 * a naopak; kontraktný test vo `wordpress/test/` to drží pri sebe.
 */
/** Verzia payloadu. Plugin a backend sa musia zhodovať. */
export const REPORT_SCHEMA_VERSION = 1;
/**
 * Typ SiteComponentu. WP core je zámerne ďalší komponent (slug `wordpress`),
 * aby matching nemal zvláštnu vetvu — pozri CONTEXT.md, SiteComponent.
 */
export const ComponentTypeSchema = z.enum(['core', 'plugin', 'theme']);
/**
 * Slug musí sedieť s Wordfence slugmi, inak sa komponent nenamatchuje
 * a dashboard tvrdí „0 zraniteľností". Schéma ho ale ZÁMERNE nefiltruje:
 * jeden plugin s divným adresárom nesmie zneplatniť celý report —
 * mismatch patrí do panelu pokrytia (ticket 11), nie do chyby parsovania.
 */
const SlugSchema = z.string().min(1).max(190);
/** Verzia je voľný reťazec — WordPress verzie nie sú semver (`2.5.3.1`, `1.0-beta2`). */
const VersionSchema = z.string().min(1).max(64);
/**
 * Nainštalovaný komponent môže mať prázdnu verziu (vlastný kód bez hlavičky).
 * Plugin ju reportuje POCTIVO ako prázdny reťazec — nič si nevymýšľa, lebo
 * vymyslená `0` by matchla rozsah `* – X` a vyrobila falošný nález.
 * Backend takému komponentu nepridelí kľúč a ukáže ho v paneli pokrytia.
 */
const InstalledVersionSchema = z.string().max(64);
export const SiteComponentReportSchema = z.object({
    type: ComponentTypeSchema,
    slug: SlugSchema,
    name: z.string().max(255),
    version: InstalledVersionSchema,
    /** Core je vždy aktívny; téma je aktívna, ak je to aktuálna téma alebo jej rodič. */
    active: z.boolean(),
    /** Verzia, na ktorú sa dá aktualizovať hneď, alebo null. */
    update_available: VersionSchema.nullable(),
    /**
     * `plugin_file` pre plugin, adresár šablóny pre tému, null pre core.
     * Drží sa v payloade zámerne: slug mismatch je najtichšie zlyhanie systému
     * a bez pôvodného súboru sa nedá dohľadať, z čoho slug vznikol.
     */
    file: z.string().max(512).nullable(),
});
/**
 * MU-pluginy sledovanej stránky. Vlastné pole, lebo do matchingu NEvstupujú —
 * je to spravidla vlastný kód bez záznamu v katalógu.
 */
export const MuPluginReportSchema = z.object({
    slug: z.string().min(1).max(190),
    name: z.string().max(255),
    version: VersionSchema.nullable(),
    file: z.string().max(512),
});
/** Známa výstraha. Reťazec je zámerne otvorený, aby novší plugin neprelomil starší backend. */
export const WARNING_MULTISITE_UNSUPPORTED = 'multisite_unsupported';
const IsoInstantSchema = z.iso.datetime({ offset: false });
export const SiteReportSchema = z.object({
    schema_version: z.literal(REPORT_SCHEMA_VERSION),
    generated_at: IsoInstantSchema,
    site: z.object({
        url: z.url(),
        wp_version: VersionSchema,
        php_version: VersionSchema,
        is_multisite: z.boolean(),
        /**
         * `false` znamená, že inventár je NEÚPLNÝ (multisite: vraciame len hlavnú sieť).
         * Backend to nesmie zamlčať — nula nálezov z neúplného inventára nie je čistá stránka.
         */
        inventory_complete: z.boolean(),
    }),
    components: z.array(SiteComponentReportSchema),
    mu_plugins: z.array(MuPluginReportSchema),
    /**
     * Kedy WordPress naposledy kontroloval aktualizácie. Plugin kontrolu NEvynucuje
     * (to by na klientskom webe stálo sekundy), takže `update_available` môže byť starý.
     */
    updates_last_checked: z.object({
        core: IsoInstantSchema.nullable(),
        plugins: IsoInstantSchema.nullable(),
        themes: IsoInstantSchema.nullable(),
    }),
    warnings: z.array(z.string().max(120)),
});
